import {
  Component,
  ElementRef,
  inject,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { ApiService } from '../../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Socket } from 'ngx-socket-io';
import { TAGInvItemType, TAGInvTransaction } from '../../../../models/taginventory';
import { MessageService } from 'primeng/api';
import { PrinterService } from '../../../printer.service';
import { DatePipe } from '@angular/common';
import {
  BarcodeScannerSearchField,
  CodeScannedEvent,
} from '../../../components/barcode-scanner-search-field/barcode-scanner-search-field';
import { findInvByCode } from '../inventoryHelper';
import { MatIcon } from '../../../components/mat-icon/mat-icon';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { dialogConfig } from '../../../dialogs';
import { ItemEditDialog, ItemEditDialogData } from '../item-edit-dialog/itemEditDialog';

export interface TransactionEditDialogData {
  transactionId: string;
  socket: Socket;
}

@Component({
  selector: 'app-internal-transaction-edit-dialog',
  imports: [
    TableModule,
    SkeletonModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    FluidModule,
    ReactiveFormsModule,
    FloatLabelModule,
    TextareaModule,
    DatePickerModule,
    TranslatePipe,
    ToggleSwitchModule,
    DatePipe,
    BarcodeScannerSearchField,
    MatIcon,
    ProgressSpinnerModule,
    FormsModule,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './transactionEditDialog.html',
  styleUrl: './transactionEditDialog.scss',
})
export class TransactionEditDialog {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  public api = inject(ApiService);
  public printer = inject(PrinterService);

  @ViewChild(BarcodeScannerSearchField) search: BarcodeScannerSearchField;

  @ViewChildren('instanceElements') instanceElements!: QueryList<ElementRef>;
  @ViewChildren('typeElements') typeElements!: QueryList<ElementRef>;

  transaction = signal<TAGInvTransaction>(new TAGInvTransaction());
  loadingTransaction = signal(true);
  itemTypes = signal<TAGInvItemType[]>([]);
  itemTypesCount = signal<Record<string, number>>({});

  collapsedTypes = signal<Record<string, boolean>>({});

  username = signal('');

  updatingItems = signal<{ itemid: string; instanceid: string }[]>([]);
  scrollToItemId = signal<string | null>(null);

  transactionID: string = '';

  isAddNew = signal(false);
  additionalReturns = signal(0);

  constructor(
    public dialogRef: DynamicDialogRef<TransactionEditDialog>,
    public config: DynamicDialogConfig<TransactionEditDialogData>,
  ) {
    this.transactionID = config.data?.transactionId ?? '';

    config.header = this.translate.translate('internal.inventory.transactionEdit.title')();
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.api
        .getInvTransaction(this.transactionID)
        .then((invTransaction: TAGInvTransaction) => {
          this.transaction.set(invTransaction);

          this.updateValues();

          this.api
            .getUser(this.transaction().user)
            .then((user) => {
              this.username.set(user.fullname);
            })
            .catch((reason) => {});

          this.loadingTransaction.set(false);
          this.initSocketHandler();
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotLoadInventoryTransaction')(),
            detail: reason.message,
          });

          this.dialogRef.close();
        });
    });
  }

  /*   ngAfterViewInit() {
    this.resetScannerField();
    setTimeout(() => this.scannerInput?.focus(), 200);
  } */

  ngOnDestroy(): void {
    this.config.data.socket.removeListener(
      'updateTransaction',
      this.socket_onUpdateTransaction.bind(this),
    );
  }

  initSocketHandler() {
    this.config.data.socket.on('updateTransaction', this.socket_onUpdateTransaction.bind(this));
  }

  socket_onUpdateTransaction(data: any) {
    let newTrans = new TAGInvTransaction(data.transaction);

    if (newTrans._id == this.transaction()._id) {
      console.log('Transaction updated', newTrans);
      this.transaction.set(newTrans);
      this.updateValues();
    }
  }

  instanceDeleted(itemid: string, instanceid: string) {
    var item = this.itemTypes().find((itm) => itm._id == itemid);
    if (!item) return false;

    var instance = item.instances.find((inst) => inst.itemid == instanceid);
    if (!instance) return false;

    return item.deleted || instance.deleted;
  }

  instanceUpdating(itemid: string, instanceid: string) {
    return (
      this.updatingItems().findIndex(
        (itm) => itm.itemid == itemid && itm.instanceid == instanceid,
      ) != -1
    );
  }

  toggleCollapse(typeId: string): void {
    this.collapsedTypes.update((ct) => {
      ct[typeId] = !ct[typeId];
      return Object.assign({}, ct);
    });
  }

  itemFilter(itemid: string) {
    return this.transaction().items.filter((comb) => comb.itemid == itemid);
  }

  itemComplete(itemid: string) {
    return this.itemFilter(itemid).filter((comb) => comb.back == null).length == 0;
  }

  updateValues() {
    this.api
      .getInvItems()
      .then((items) => {
        this.itemTypes.set(
          items.filter(
            (item) => this.transaction().items.findIndex((itm) => itm.itemid == item._id) != -1,
          ),
        );
        this.itemTypesCount.set(
          this.itemTypes().reduce(
            (acc, item) => {
              const count = this.transaction().items.filter(
                (itm) => itm.itemid === item._id && !itm.back,
              ).length;
              if (count > 0) {
                acc[item._id] = count;
              } else {
                acc[item._id] = this.transaction().items.filter(
                  (itm) => itm.itemid === item._id,
                ).length;
              }
              return acc;
            },
            {} as Record<string, number>,
          ),
        );

        if (Object.keys(this.collapsedTypes()).length === 0) {
          this.collapsedTypes.set(
            this.itemTypes().reduce(
              (acc, item) => {
                const count = this.transaction().items.filter(
                  (itm) => itm.itemid === item._id && !itm.back,
                ).length;
                if (count > 0) {
                  acc[item._id] = false;
                } else {
                  acc[item._id] = true;
                }
                return acc;
              },
              {} as Record<string, boolean>,
            ),
          );
        }

        if (this.transaction().allItemsBack() && this.transaction().items.length > 0) {
          // TODO: Disable Scanner Input
          this.search.disabled = true;
        } else {
          this.search.disabled = false;
          // TODO: Enable Scanner Input
        }

        if (this.scrollToItemId) {
          setTimeout(() => this.scrollToItem(), 100); // small delay to ensure DOM updated
        }
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadInventoryItems')(),
          detail: reason.message,
        });

        this.dialogRef.close();
      });
  }

  async codeScanned(code: CodeScannedEvent) {
    this.search.busy = true;

    let { item, instance } = await findInvByCode(
      code.code,
      undefined,
      undefined,
      undefined,
      this.api,
    );

    if (item) {
      if (instance) {
        await this.itemScanned(item._id, instance.itemid);
      } else {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('internal.inventory.general.noInstanceID')(),
        });
      }
    } else {
      try {
        let group = await this.api.getInvItemGroup(code.code);
        if (group) {
          if (this.api.config().allowGroupsForBookIn || this.isAddNew()) {
            for (let gItem of group.items) {
              await this.itemScanned(gItem.itemid, gItem.instanceid);
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.translate(
                'internal.inventory.general.groupForbidden',
              )(),
            });
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('internal.inventory.inventoryCheck.notFound')(),
            detail: this.translate.translate('internal.inventory.general.couldNotFindItemOrGroup')(),
          });
        }
      } catch (reason) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('internal.inventory.general.couldNotFindItemOrGroup')(),
          detail: reason.message,
        });
      }
    }

    this.search.busy = false;
    this.search.reset();
  }

  async itemScanned(itemID: string, instID: string) {
    if (this.isAddNew()) {
      var index: number = this.transaction().items.findIndex(
        (comb) => comb.itemid == itemID && comb.instanceid == instID,
      );
      if (index == -1 || this.transaction().items[index].back != null) {
        await this.addNewInstance(itemID, instID);
      } else if (index !== -1 && this.transaction().items[index].back == null) {
        this.scrollToItemId.set(itemID + '-' + instID);
        this.scrollToItem();
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.translate(
            'internal.inventory.general.alreadyCheckedOut',
          )(),
        });
      }
    } else {
      var index: number = this.transaction().items.findIndex(
        (comb) => comb.itemid == itemID && comb.instanceid == instID,
      );
      if (index != -1 && this.transaction().items[index].back == null) {
        await this.returnInstance(itemID, instID);
      } else if (index == -1) {

        if (this.api.config().allowReturnItemsNotInTransaction) {

          try {
            let transactions = await this.api.getInvTransactionsByItem(itemID);
            let filteredTransactions = transactions.filter((t) => {
              let line = t.findItem(itemID, instID);
              if (!line) return false;

              if (line.back != null) return false;

              return true;
            });

            for (let t of filteredTransactions) {
              try {
                let result = await this.api.returnTransactionItems(t._id, [{itemid: itemID, instanceid: instID}]);
                this.messageService.add({
                  severity: 'success',
                  summary: this.translate.translate('internal.inventory.general.itemReturned')(),
                });
                this.additionalReturns.update(ar => ar + 1);
              } catch (err) {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translate.translate(
                    'internal.inventory.transactionEdit.couldNotReturnItem',
                  )(),
                  detail: err.message
                });
              }
            }
          } catch(reason) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.translate(
                'internal.inventory.transactionEdit.couldNotReturnItem',
              )(),
              detail: reason.message
            });
          }


        } else {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate(
              'internal.inventory.general.notInTransaction',
            )(),
          });
        }


      } else if (this.transaction().items[index].back !== null) {
        this.scrollToItemId.set(itemID + '-' + instID);
        this.scrollToItem();
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.translate(
            'internal.inventory.general.alreadyCheckedIn',
          )(),
        });
      }
    }
  }

  async returnInstance(itemid: string, instanceid: string) {
    this.updatingItems.update((ui) => {
      ui.push({ itemid: itemid, instanceid: instanceid });
      return [...ui];
    });

    this.scrollToItemId.set(itemid + '-' + instanceid);
    this.scrollToItem();

    try {
      let result = await this.api.returnTransactionItems(this.transaction()._id, [
        { itemid: itemid, instanceid: instanceid },
      ]);
      this.updatingItems.update((ui) => {
        return ui.filter((itm) => itm.itemid != itemid && itm.instanceid != instanceid);
      });

      this.transaction.set(result);

      const count = this.transaction().items.filter(
        (itm) => itm.itemid === itemid && !itm.back,
      ).length;
      if (count === 0) {
        this.collapsedTypes.update((ct) => {
          ct[itemid] = true;
          return Object.assign({}, ct);
        });

        this.scrollToItemId.set(itemid);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.translate(
            'internal.inventory.general.itemTypeReturned',
          )(),
        });

        setTimeout(() => {
          this.updateValues();
        }, 100);
      } else {
        this.scrollToItemId.set(itemid + '-' + instanceid);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.translate('internal.inventory.general.itemReturned')(),
        });
        this.updateValues();
      }
    } catch (reason) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('internal.inventory.transactionEdit.couldNotReturnItem')(),
        detail: reason.message,
      });
      console.error(reason);
    }
  }

  async addNewInstance(itemid: string, instanceid: string) {
    this.updatingItems.update((ui) => {
      ui.push({ itemid: itemid, instanceid: instanceid });
      return [...ui];
    });
    this.scrollToItemId.set(itemid + '-' + instanceid);
    this.updateValues();

    try {
      let result = await this.api.addTransactionItems(this.transaction()._id, [
        { itemid: itemid, instanceid: instanceid },
      ]);
      this.updatingItems.update((ui) => {
        return ui.filter((itm) => itm.itemid != itemid && itm.instanceid != instanceid);
      });

      this.transaction.set(result);
      this.scrollToItemId.set(itemid + '-' + instanceid);
      this.updateValues();
      this.messageService.add({
        severity: 'success',
        summary: this.translate.translate('internal.inventory.general.itemAdded')(),
      });
    } catch (reason) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('internal.inventory.transactionEdit.couldNotAddNewItem')(),
        detail: reason.message,
      });
      console.error(reason);
    }
  }

  printLabel(item: TAGInvItemType, instance: { itemid: string }) {
    if (!this.printer.isConnected())
      this.printer
        .connectToPrinter()
        .then((result) => {
          this.printer.printItemLabels(item, [instance]);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotConnectToPrinter')(),
            detail: reason.message,
          });
        });
    else this.printer.printItemLabels(item, [instance]);
  }

  selectItem(item: TAGInvItemType, selectedInstances?: string[]) {
    const itemEditRef = this.dialogService.open(
      ItemEditDialog,
      dialogConfig<ItemEditDialogData>({
        data: {
          item: item,
          selectedInstances: selectedInstances,
          socket: this.config.data.socket,
        },
      }),
    );

    itemEditRef.onClose.subscribe((result) => {
      this.search?.reset();
    });
  }

  scrollToItem() {
    if (!this.scrollToItemId()) return;

    // Item Type
    if (this.scrollToItemId().indexOf('-') === -1) {
      const target = this.typeElements
        .toArray()
        .find((el) => el.nativeElement.id === this.scrollToItemId());

      if (target) {
        target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Remove the shaking after 2 seconds
      setTimeout(() => {
        this.scrollToItemId.set(null);
      }, 2000);
    } else {
      //Item Instance
      const target = this.instanceElements
        .toArray()
        .find((el) => el.nativeElement.id === this.scrollToItemId());

      if (target) {
        if (this.collapsedTypes()[this.scrollToItemId().split('-')[0]]) {
          this.collapsedTypes.update((ct) => {
            ct[this.scrollToItemId().split('-')[0]] = false;
            return Object.assign({}, ct);
          });

          //this.cdr.detectChanges();  // Manually trigger change detection
          setTimeout(() => {
            target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        } else {
          target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Remove the shaking after 2 seconds
        setTimeout(() => {
          this.scrollToItemId.set(null);
        }, 2000);
      } else {
        console.error('Element not found', this.scrollToItemId());
      }
    }
  }

  instanceShaking(itemid: string, instanceid: string) {
    return itemid + '-' + instanceid === this.scrollToItemId();
  }

  typeShaking(itemid: string) {
    return itemid === this.scrollToItemId();
  }
}
